import React, { useState, useEffect, useMemo } from "react";
import { useVideoPlayer } from "./VideoPlayerProvider";
import background from "./images/background.png";
import karimeAvatar from "./images/karime-logo-2026.png";

const CACHE_KEY = "youtube_feed_cache";
const CACHE_DURATION = 6 * 60 * 60 * 1000; // 6 horas (antes 3 días)
const MIN_REFRESH_GAP_MS = 6 * 60 * 60 * 1000; // 6 horas (antes 24h)

const formatNumber = (num) => {
  if (num === null || num === undefined) return "0";
  if (typeof num === "string") {
    num = parseInt(num, 10);
    if (isNaN(num)) return "0";
  }
  if (num >= 1_000_000_000)
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
  if (num >= 1_000_000)
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return num.toString();
};

// ── Section Header ────────────────────────────────────────────────────────────
function SectionHeader({ title }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span
        className="text-xs font-bold tracking-widest uppercase"
        style={{ color: "#b06ab3", letterSpacing: "0.15em" }}
      >
        {title}
      </span>
      <div
        className="flex-1 h-px"
        style={{
          background: "linear-gradient(to right, #b06ab320, transparent)",
        }}
      />
    </div>
  );
}

// ── Featured Videos — horizontal scroll carousel ──────────────────────────────
function FeaturedSection({ videos, onPlay }) {
  return (
    <section className="mb-8">
      <SectionHeader title="Karime Kooler · Temporada 8" />
      <div
        className="flex gap-3 overflow-x-auto pb-2"
        style={{ scrollbarWidth: "none" }}
      >
        {videos.map((video) => (
          <div
            key={video.id}
            onClick={() => onPlay(video.id)}
            className="flex-shrink-0 cursor-pointer group"
            style={{ width: "200px" }}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.75)",
                borderRadius: "16px",
                overflow: "hidden",
                backdropFilter: "blur(8px)",
                boxShadow: "0 2px 12px rgba(176,106,179,0.08)",
                transition: "box-shadow 0.2s, transform 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 6px 24px rgba(176,106,179,0.18)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 2px 12px rgba(176,106,179,0.08)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {/* Thumbnail */}
              <div className="relative">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  style={{
                    width: "200px",
                    height: "112px",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                <div
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100"
                  style={{
                    background: "rgba(176,106,179,0.45)",
                    transition: "opacity 0.2s",
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      background: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="#b06ab3"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
              {/* Info */}
              <div className="p-2">
                <p
                  className="text-xs font-semibold line-clamp-2 mb-1"
                  style={{ color: "#2d1f3d", lineHeight: "1.3" }}
                >
                  {video.title}
                </p>
                <p className="text-xs" style={{ color: "#9b7eaa" }}>
                  {formatNumber(video.viewCount)} visualizaciones
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Vlogs — horizontal scroll carousel, most recent content ─────────────────
function VlogsSection({ videos, onPlay }) {
  return (
    <section className="mb-8">
      <SectionHeader title="Vlogs " />
      <div
        className="flex gap-3 overflow-x-auto pb-2"
        style={{ scrollbarWidth: "none" }}
      >
        {videos.map((video) => (
          <div
            key={video.id}
            onClick={() => onPlay(video.id)}
            className="flex-shrink-0 cursor-pointer group"
            style={{ width: "200px" }}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.75)",
                borderRadius: "16px",
                overflow: "hidden",
                backdropFilter: "blur(8px)",
                boxShadow: "0 2px 12px rgba(176,106,179,0.08)",
                transition: "box-shadow 0.2s, transform 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 6px 24px rgba(176,106,179,0.18)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 2px 12px rgba(176,106,179,0.08)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div className="relative">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  style={{
                    width: "200px",
                    height: "112px",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                <div
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100"
                  style={{
                    background: "rgba(176,106,179,0.45)",
                    transition: "opacity 0.2s",
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      background: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="#b06ab3"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="p-2">
                <p
                  className="text-xs font-semibold line-clamp-2 mb-1"
                  style={{ color: "#2d1f3d", lineHeight: "1.3" }}
                >
                  {video.title}
                </p>
                <p className="text-xs" style={{ color: "#9b7eaa" }}>
                  {formatNumber(video.viewCount)} visualizaciones
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Music Videos — horizontal scroll, square cards ───────────────────────────
function MusicSection({ videos, onPlay }) {
  return (
    <section className="mb-8">
      <SectionHeader title="Videos Musicales" />
      <div
        className="flex gap-3 overflow-x-auto pb-2"
        style={{ scrollbarWidth: "none" }}
      >
        {videos.map((video) => (
          <div
            key={video.id}
            onClick={() => onPlay(video.id)}
            className="flex-shrink-0 cursor-pointer group"
            style={{ width: "140px" }}
          >
            <div
              className="relative mb-2"
              style={{ borderRadius: "14px", overflow: "hidden" }}
            >
              <img
                src={video.thumbnail}
                alt={video.title}
                style={{
                  width: "140px",
                  height: "140px",
                  objectFit: "cover",
                  display: "block",
                  transition: "transform 0.2s",
                }}
                className="group-hover:scale-105"
              />
              <div
                className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100"
                style={{
                  background: "rgba(176,106,179,0.5)",
                  transition: "opacity 0.2s",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="#b06ab3"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </div>
            <p
              className="text-xs font-semibold text-center line-clamp-1"
              style={{ color: "#2d1f3d" }}
            >
              {video.title}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Canal Feed — 2-col grid, all videos including shorts ─────────────────────
const FEED_INITIAL = 8;

function FeedSection({ videos, loading, error, onPlay, onRetry }) {
  const [showAll, setShowAll] = useState(false);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div
          className="animate-spin rounded-full h-7 w-7 border-2"
          style={{ borderColor: "#b06ab3", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="p-4 rounded-2xl text-sm mb-4"
        style={{ background: "rgba(255,100,100,0.1)", color: "#c0392b" }}
      >
        {error}
        <button
          onClick={onRetry}
          className="block mt-2 font-semibold underline"
        >
          Intentar de nuevo
        </button>
      </div>
    );
  }

  if (!videos.length) {
    return (
      <p className="text-center py-8 text-sm" style={{ color: "#9b7eaa" }}>
        No hay videos disponibles
      </p>
    );
  }

  const visible = showAll ? videos : videos.slice(0, FEED_INITIAL);
  const hasMore = videos.length > FEED_INITIAL;

  return (
    <div>
      <div className="grid grid-cols-2 gap-3">
        {visible.map((video) => (
          <div
            key={video.id}
            onClick={() => onPlay(video.id)}
            className="cursor-pointer group"
            style={{
              background: "rgba(255,255,255,0.75)",
              borderRadius: "16px",
              overflow: "hidden",
              backdropFilter: "blur(8px)",
              boxShadow: "0 2px 8px rgba(176,106,179,0.08)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 8px 20px rgba(176,106,179,0.16)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 2px 8px rgba(176,106,179,0.08)";
            }}
          >
            <div className="relative">
              <img
                src={video.thumbnail}
                alt={video.title}
                style={{
                  width: "100%",
                  aspectRatio: "16/9",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 flex items-center justify-center"
                style={{
                  background: "rgba(176,106,179,0.4)",
                  transition: "opacity 0.2s",
                }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
            <div className="p-2">
              <p
                className="text-xs font-semibold line-clamp-2 mb-1"
                style={{ color: "#2d1f3d", lineHeight: "1.3" }}
              >
                {video.title}
              </p>
              <p className="text-xs" style={{ color: "#9b7eaa" }}>
                {formatNumber(video.viewCount)} vistas
              </p>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full mt-4 py-3 rounded-2xl text-sm font-semibold"
          style={{
            background: "rgba(255,255,255,0.75)",
            color: "#b06ab3",
            border: "1.5px solid rgba(176,106,179,0.3)",
            backdropFilter: "blur(8px)",
            cursor: "pointer",
          }}
        >
          {showAll ? "Ver menos ↑" : "Ver más ↓"}
        </button>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function YouTubeFeed() {
  const [feedVideos, setFeedVideos] = useState([]);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [musicVideos, setMusicVideos] = useState([]);
  const [vlogVideos, setVlogVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const { play } = useVideoPlayer();

  const channelId = "UCCQBSZfNtWtY6oxo23iLCpQ";
  const vlogsPlaylistId = "PLEC6BImB23TA";

  const formatUpdated = useMemo(() => {
    if (!lastUpdated) return null;
    try {
      return new Date(lastUpdated).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return null;
    }
  }, [lastUpdated]);

  // ── Cache helpers ──────────────────────────────────────────────────────────
  const getCached = () => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const { data, timestamp } = JSON.parse(raw);
      if (Date.now() - timestamp > CACHE_DURATION) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      return { data, timestamp };
    } catch {
      return null;
    }
  };

  const setCached = (data) => {
    try {
      const payload = { data, timestamp: Date.now() };
      localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
      setLastUpdated(payload.timestamp);
    } catch {}
  };

  const clearCache = () => {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {}
  };

  // ── Static data loaders ────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`/data/featuredVideos.json?v=${Date.now()}`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setFeaturedVideos)
      .catch(() => {});

    fetch(`/data/musicVideos.json?v=${Date.now()}`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setMusicVideos)
      .catch(() => {});
  }, []);

  // ── Vlogs loader (playlist "Karime Vlogs") ─────────────────────────────────
  useEffect(() => {
    fetch(`/api/youtube/playlist-videos/${vlogsPlaylistId}`, {
      cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : []))
      .then(setVlogVideos)
      .catch(() => {});
  }, [vlogsPlaylistId]);

  // ── Feed fetch ─────────────────────────────────────────────────────────────
  const fetchFeed = async (ignoreCache = false) => {
    if (ignoreCache) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      if (!ignoreCache) {
        const cached = getCached();
        if (cached?.data) {
          setFeedVideos(cached.data);
          setLastUpdated(cached.timestamp);
          return;
        }
      }

      const url = ignoreCache
        ? `/api/youtube/channel-videos/${channelId}?force=1`
        : `/api/youtube/channel-videos/${channelId}`;

      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok)
        throw new Error((await res.text()) || "Error al cargar videos");
      const data = await res.json();

      setFeedVideos(data);
      setCached(data);
    } catch (err) {
      setError(err?.message || "Error inesperado");
    } finally {
      if (ignoreCache) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    if (channelId) fetchFeed();
  }, [channelId]);

  // ── Keyboard shortcut ──────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (
        e.key.toLowerCase() === "r" &&
        !e.metaKey &&
        !e.ctrlKey &&
        canManualRefresh
      ) {
        e.preventDefault();
        clearCache();
        fetchFeed(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const canManualRefresh =
    !lastUpdated || Date.now() - lastUpdated > MIN_REFRESH_GAP_MS;

  return (
    <div
      className="flex flex-col items-center min-h-screen p-5"
      style={{
        backgroundImage: `url(${background})`,
        backgroundRepeat: "repeat-y",
        backgroundSize: "100% auto",
        backgroundPosition: "top center",
      }}
    >
      {/* ── Header nuevo ── */}
      <div
        className="w-full max-w-xl font-quicksand mb-6"
        style={{ position: "relative", paddingTop: "40px", marginTop: "12px" }}
      >
        {/* Foto saliendo por arriba */}
        <img
          alt="Karime Pindter"
          //src={karimeAvatar}
          src="https://res.cloudinary.com/dbvqeiqxb/image/upload/v1788989549/7a9cad14-c84e-4fea-8533-8fba359f94de_pmz8hy.png"
          style={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            border: "2px solid white",
            objectFit: "cover",
            objectPosition: "top",
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
            background: "#ede9fe",
            boxShadow: "0 2px 10px rgba(124, 58, 237, 0.25)",
          }}
        />

        {/* Card */}
        <div
          style={{
            backdropFilter: "blur(8px)",
            padding: "45px 20px 13px",
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontSize: "20px",
              fontWeight: "700",
              color: "#000000",
              lineHeight: 1.2,
              margin: 0,
            }}
          >
            Canal oficial de YouTube ✨
          </p>
          <p
            style={{
              fontSize: "12px",
              color: "#b06ab3",
              marginTop: "4px",
              opacity: 0.85,
            }}
          >
            Vlogs · Videos · Música · Shorts
          </p>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="w-full max-w-xl font-quicksand">
        {/* Sección 0 — Vlogs (contenido más nuevo, va primero) */}
        {vlogVideos.length > 0 && (
          <VlogsSection videos={vlogVideos} onPlay={play} />
        )}
        {/* Sección 1 — Videos Destacados */}
        {featuredVideos.length > 0 && (
          <FeaturedSection videos={featuredVideos} onPlay={play} />
        )}
        {/* Sección 2 — Videos Musicales */}
        {musicVideos.length > 0 && (
          <MusicSection videos={musicVideos} onPlay={play} />
        )}
        {/* Sección 3 — Youtube Feed (sin los vlogs, ya tienen su sección) */}
        <section>
          <SectionHeader title="Canal Feed (Shorts & Más)" />
          <FeedSection
            videos={feedVideos.filter(
              (v) => !vlogVideos.some((vlog) => vlog.id === v.id)
            )}
            loading={loading}
            error={error}
            onPlay={play}
            onRetry={() => fetchFeed(true)}
          />
        </section>
      </div>
    </div>
  );
}
